'use client';
import React, { useState, useRef, useCallback } from 'react';

interface ImageFile {
  file: File;
  url: string;
  name: string;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ImagesToPDFTool() {
  const [images, setImages] = useState<ImageFile[]>([]);
  const [pageSize, setPageSize] = useState<'A4' | 'Letter' | 'fit'>('A4');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const addImages = useCallback((files: FileList | null) => {
    if (!files) return;
    const imgs = Array.from(files).filter((f) => f.type.match(/^image\/(jpeg|png|webp)$/));
    if (imgs.length === 0) { alert('Please upload JPG, PNG, or WebP images.'); return; }
    setImages((prev) => [...prev, ...imgs.map((f) => ({ file: f, url: URL.createObjectURL(f), name: f.name }))]);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => addImages(e.target.files);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    addImages(e.dataTransfer.files);
  };

  const removeImage = (index: number) => setImages((prev) => prev.filter((_, i) => i !== index));

  const moveImage = (index: number, dir: 'up' | 'down') => {
    const arr = [...images];
    const target = dir === 'up' ? index - 1 : index + 1;
    if (target < 0 || target >= arr.length) return;
    [arr[index], arr[target]] = [arr[target], arr[index]];
    setImages(arr);
  };

  const handleConvert = async () => {
    if (images.length === 0) { alert('Please add at least one image.'); return; }
    setProcessing(true);
    setProgress(0);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const pdf = await PDFDocument.create();
      const PAGE_SIZES: Record<string, [number, number]> = { A4: [595.28, 841.89], Letter: [612, 792] };

      for (let i = 0; i < images.length; i++) {
        setProgress(Math.round(((i + 1) / images.length) * 90));
        const imgFile = images[i];

        // Convert to JPEG via canvas for WebP support
        const imgEl = new window.Image();
        imgEl.src = imgFile.url;
        await new Promise<void>((resolve) => { imgEl.onload = () => resolve(); if (imgEl.complete) resolve(); });
        const canvas = document.createElement('canvas');
        canvas.width = imgEl.naturalWidth;
        canvas.height = imgEl.naturalHeight;
        const ctx = canvas.getContext('2d');
        if (!ctx) continue;
        ctx.drawImage(imgEl, 0, 0);
        const jpegDataUrl = canvas.toDataURL('image/jpeg', 0.92);
        const base64 = jpegDataUrl.split(',')[1];
        const binaryStr = atob(base64);
        const bytes = new Uint8Array(binaryStr.length);
        for (let j = 0; j < binaryStr.length; j++) bytes[j] = binaryStr.charCodeAt(j);

        const embeddedImage = await pdf.embedJpg(bytes.buffer);
        let pageW: number, pageH: number;
        if (pageSize === 'fit') {
          pageW = embeddedImage.width;
          pageH = embeddedImage.height;
        } else {
          [pageW, pageH] = PAGE_SIZES[pageSize];
        }
        const page = pdf.addPage([pageW, pageH]);
        const s = Math.min(pageW / embeddedImage.width, pageH / embeddedImage.height);
        const dw = embeddedImage.width * s;
        const dh = embeddedImage.height * s;
        page.drawImage(embeddedImage, { x: (pageW - dw) / 2, y: (pageH - dh) / 2, width: dw, height: dh });
      }

      setProgress(95);
      const pdfBytes = await pdf.save();
      setProgress(100);
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'images-to-pdf.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      alert('Conversion failed. Please try again.');
    } finally {
      setProcessing(false);
      setProgress(0);
    }
  };

  return (
    <div className="space-y-5">
      <div
        className={`upload-zone rounded-2xl p-8 text-center cursor-pointer ${isDragging ? 'drag-over' : ''}`}
        onClick={() => fileInputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
        onDragLeave={() => setIsDragging(false)}
        onDrop={handleDrop}
      >
        <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-4">
          <svg className="w-7 h-7 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
          </svg>
        </div>
        <p className="font-semibold text-text-primary mb-1">Add Images</p>
        <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
        <p className="text-text-muted text-xs mt-1">JPG, PNG, WebP supported</p>
        <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" multiple className="hidden" onChange={handleFileChange} />
      </div>

      {images.length > 0 && (
        <>
          <div className="space-y-2">
            {images.map((img, i) => (
              <div key={i} className="flex items-center gap-3 bg-surface rounded-xl p-3">
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={img.url} alt={img.name} className="w-10 h-10 object-cover rounded-lg flex-shrink-0" />
                <p className="flex-1 text-sm text-text-primary truncate">{img.name}</p>
                <p className="text-xs text-text-muted flex-shrink-0">{formatBytes(img.file.size)}</p>
                <div className="flex gap-1">
                  <button onClick={() => moveImage(i, 'up')} disabled={i === 0} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors">
                    <svg className="w-3.5 h-3.5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 15.75l7.5-7.5 7.5 7.5" /></svg>
                  </button>
                  <button onClick={() => moveImage(i, 'down')} disabled={i === images.length - 1} className="p-1 rounded hover:bg-gray-200 disabled:opacity-30 transition-colors">
                    <svg className="w-3.5 h-3.5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" /></svg>
                  </button>
                  <button onClick={() => removeImage(i)} className="p-1 rounded hover:bg-red-50 transition-colors">
                    <svg className="w-3.5 h-3.5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div>
            <label className="font-semibold text-text-primary text-sm mb-2 block">Page Size</label>
            <div className="flex gap-2">
              {(['A4', 'Letter', 'fit'] as const).map((s) => (
                <button key={s} onClick={() => setPageSize(s)} className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${pageSize === s ? 'bg-brand-blue text-white' : 'bg-surface text-text-secondary hover:bg-gray-100'}`}>
                  {s === 'fit' ? 'Fit to Image' : s}
                </button>
              ))}
            </div>
          </div>

          {processing && (
            <div>
              <div className="flex justify-between text-xs text-text-muted mb-1"><span>Converting…</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-brand-blue rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}

          <button
            onClick={handleConvert}
            disabled={processing}
            className="btn-primary w-full text-base py-3.5 disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {processing ? (
              <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>Converting…</>
            ) : (
              <>Convert {images.length} Image{images.length !== 1 ? 's' : ''} to PDF</>
            )}
          </button>
        </>
      )}
    </div>
  );
}
